/*
練習道場の一覧 (/luagate/dojo) に並べるカードの決まり。

# 何を並べるか
公開中の道場のコース (slug が dojo- で始まるもの) だけ。公開かどうかはコース一覧の publishStatus で決め、
画面は API の一覧を正とする (course-slugs.generated.ts は検査用の表で、画面には使わない)。

# 並び順
画面に出すときは難易度順 (入門、初級、中級、上級)。同じ難易度の中は、tracks.ts のトラックを上から読み、type が dojo の項目が初めて出た順。
どのトラックにも載っていない公開中の道場は、最後に slug 順で足す。難易度の表 (dojo-level.ts) に無い道場は一番後ろ。
(一覧の説明は「入門から上級の順」。トラック順だけだと難度が入り混じっていた)

# クラウドエディターだけの道場
コースが無く、クラウドエディターの章 (guide-index.ts の dojo を持つ 1 章 10 問の章) だけがある道場も、同じ列に足す。
章の ID を道場の slug として使い、公開中のコースと slug が重なる道場は足さない (コースの側のカードを正とする)。
並びは、コースの道場の後ろに、EDITOR_DOJO_ORDER の順 (入門から上級)。表に無い章は、その後ろに索引の順。見せる相手は canSeePathCloudEditor が true の人だけで、
staff のあいだは受講生には 1 枚も出ない。押すとその章をクラウドエディターで開き、進みは章で通した手順の数 (n / 10)。

# クラウドエディターで開く
その道場の章がクラウドエディターにある道場だけ、1 章目の章 (DOJO_FIRST_GUIDES) を出す。
見せる相手は canSeePathCloudEditor が true の人 (PATH_CLOUDEDITOR_AUDIENCE が staff のあいだは管理者と講師だけ)。
*/
import type { GraphqlCourseSummary, UserCourseProgress } from "@noiman/shared-graphql";
import { findGuideEntry, guideIds } from "@/pages/luagate/cloudeditor/guide-index";
import { TRACKS } from "@/lib/tracks";
import type { HandIconName } from "@/components/luagate-ui/icons/registry";
import { dojoLevelOf } from "@/lib/luagate/dojo-level";
import { canSeePathCloudEditor, cloudEditorGuideHref, type PathCloudEditorAudience } from "@/lib/luagate/path-cloudeditor";

/** 道場のコースの slug から、クラウドエディターでの 1 章目 (apps/client/cloudeditor-guides の dojo-*-01) */
export const DOJO_FIRST_GUIDES: Readonly<Record<string, string>> = {
  "dojo-hc": "dojo-hc-01",
  "dojo-js": "dojo-js-01",
  "dojo-ts": "dojo-ts-01",
  "dojo-java": "dojo-java-01",
  "dojo-sql": "dojo-sql-01",
  "dojo-python": "dojo-py-01",
  "dojo-algo": "dojo-al-01",
  "dojo-ex": "dojo-ex-01",
  "dojo-pr": "dojo-pr-01",
  "dojo-lx": "dojo-lx-01",
  "dojo-re": "dojo-re-01",
  "dojo-fd": "dojo-fd-01",
};

/**
 * コースの題「〔道場の名前〕 〔話の副題〕」を最初の空白で 2 つに分ける。
 * 空白が無い題、または空白の後ろが空の題は、副題を持たない 1 段の題として返す。
 */
export function splitDojoTitle(title: string): { name: string; subtitle: string | null } {
  const trimmed = title.trim();
  const match = /^(\S+)\s+(\S.*)$/su.exec(trimmed);
  if (!match) return { name: trimmed, subtitle: null };
  return { name: match[1], subtitle: match[2] };
}

/**
 * 見出しの頭に付ける印。どの道場も手描きの絵 (HandIcon の名前) にし、一覧に同じ印が 2 つ並ばないようにする
 * (汎用の練習道場の印 nav-dojo は使わない。feather は表に無い印の受け皿として型だけ残す)。技術の公式ロゴは 2026-10-19 に代表の判断でやめた (自分たちの手描きの絵で世界観をそろえるため)。
 * JavaScript、TypeScript、React、Express、Go、Java の道場は、ロゴの形を写さない技術の絵 (tech-) を使う。
 */
export type DojoIcon = { kind: "hand"; name: HandIconName } | { kind: "feather"; name: DojoFeatherName };

export type DojoFeatherName = "bug" | "cpu" | "message-square" | "monitor" | "layers" | "box" | "cloud" | "pen-tool" | "sparkles" | "git-branch" | "globe" | "lock" | "clipboard-list" | "briefcase";

export const DOJO_ICONS: Readonly<Record<string, DojoIcon>> = {
  "dojo-hc": { kind: "hand", name: "nav-html-css" },
  "dojo-js": { kind: "hand", name: "tech-javascript" },
  "dojo-ts": { kind: "hand", name: "tech-typescript" },
  "dojo-re": { kind: "hand", name: "tech-react" },
  "dojo-ex": { kind: "hand", name: "tech-express" },
  "dojo-go": { kind: "hand", name: "tech-go" },
  "dojo-java": { kind: "hand", name: "tech-java" },
  "dojo-sql": { kind: "hand", name: "chapter-06-db" },
  "dojo-python": { kind: "hand", name: "tech-python" },
  "dojo-lx": { kind: "hand", name: "chapter-04-linux" },
  // 題目の道場。デバッグは探す虫眼鏡、アルゴリズムは枝分かれの木、プロンプトは AI への吹き出し
  "dojo-fd": { kind: "hand", name: "empty-search" },
  "dojo-algo": { kind: "hand", name: "view-skillmap" },
  "dojo-pr": { kind: "hand", name: "nav-ai-tutor" },
  // クラウドエディターだけの道場
  "dojo-git": { kind: "hand", name: "chapter-08-git" },
  "dojo-cs": { kind: "hand", name: "chapter-05-cs" },
  "dojo-os": { kind: "hand", name: "cat-it-basics" },
  "dojo-docker": { kind: "hand", name: "cat-infra-cloud" },
  "dojo-cloud": { kind: "hand", name: "chapter-15-cloud" },
  "dojo-design": { kind: "hand", name: "chapter-11-fullstack" },
  "dojo-ai": { kind: "hand", name: "chapter-03-ai" },
  "dojo-web": { kind: "hand", name: "cat-network" },
  "dojo-auth": { kind: "hand", name: "state-locked" },
  "dojo-req": { kind: "hand", name: "nav-monthly-report" },
  "dojo-consul": { kind: "hand", name: "chapter-17-career" },
};

/** 練習道場のコースか。slug の頭が dojo- なら道場として扱う。 */
export function isDojoCourseSlug(slug: string | null | undefined): boolean {
  return typeof slug === "string" && slug.startsWith("dojo-");
}

export interface DojoCard {
  slug: string;
  title: string;
  /** 見出しの 1 段目 (道場の名前) と 2 段目 (話の副題) */
  name: string;
  subtitle: string | null;
  /** 話の世界の一言 (コースの説明) */
  description: string | null;
  /** 見出しの頭の印。表に無い道場は null */
  icon: DojoIcon | null;
  /** 問の数 */
  total: number;
  /** その受講生が済ませた数 */
  done: number;
  /** コースの画面 */
  href: string;
  /** クラウドエディターで開く先。見せない相手と、章が無い道場は null */
  cloudEditorHref: string | null;
  /** 体験の人に見せるだけの道場 (クラウドエディターだけの道場)。押せず、鍵の演出 (受講生のみ) を出す */
  locked?: boolean;
}

/** 道場の slug を、トラックの定義順 (入門から上級) に並べる */
export function dojoSlugsInTrackOrder(): string[] {
  const seen = new Set<string>();
  for (const track of TRACKS) {
    for (const item of track.items) {
      if (item.type === "dojo") seen.add(item.ref);
    }
  }
  return [...seen];
}

const DEFAULT_TOTAL = 10;

/** 章の ID が道場の slug の形 (dojo-lx-01 のような 1 問ずつの章は含めない) */
const EDITOR_DOJO_ID = /^dojo-[a-z]+$/;

/** クラウドエディターだけの道場を並べる順 (入門から上級)。新しい章を足したら、ここにも足す */
export const EDITOR_DOJO_ORDER: readonly string[] = ["dojo-git", "dojo-cs", "dojo-os", "dojo-web", "dojo-docker", "dojo-cloud", "dojo-auth", "dojo-design", "dojo-req", "dojo-consul", "dojo-ai"];

/** クラウドエディターだけの道場の 1 行の説明。コースの説明を持たないので、ここで持つ。題から読める範囲だけを書く */
export const EDITOR_DOJO_DESCRIPTIONS: Readonly<Record<string, string>> = {
  "dojo-git": "Git の操作を、手を動かして身につけます。",
  "dojo-cs": "コンピュータの仕組みを、コードを書きながら確かめます。",
  "dojo-os": "OS がプロセスを動かす仕組みを、自分のコードで確かめます。",
  "dojo-web": "Web とネットワークの仕組みを、コードを書いて確かめます。",
  "dojo-docker": "Docker と CI/CD を、手を動かして身につけます。",
  "dojo-cloud": "クラウドの考え方を、手を動かして確かめます。",
  "dojo-auth": "認証と認可の仕組みを、コードを書いて確かめます。",
  "dojo-design": "設計の考え方を、手を動かして身につけます。",
  "dojo-req": "要件定義の進め方を、手を動かして身につけます。",
  "dojo-consul": "IT コンサルの仕事の進め方を、手を動かして身につけます。",
  "dojo-ai": "AI の使いどころと仕組みを、手を動かして確かめます。",
};

/** コースが無く、クラウドエディターの章だけがある道場の章の ID。公開中のコースがある slug は除き、索引の順 */
export function editorOnlyDojoIds(courses: readonly Pick<GraphqlCourseSummary, "slug" | "publishStatus">[]): string[] {
  const published = new Set(courses.filter((course) => course.publishStatus === "published").map((course) => course.slug));
  const ids = guideIds().filter((id) => EDITOR_DOJO_ID.test(id) && findGuideEntry(id)?.dojo && !published.has(id));
  const rank = (id: string) => (EDITOR_DOJO_ORDER.includes(id) ? EDITOR_DOJO_ORDER.indexOf(id) : EDITOR_DOJO_ORDER.length);
  return ids.sort((a, b) => rank(a) - rank(b));
}

export function buildDojoCards({
  courses,
  progress,
  memberRole,
  pathReady,
  audience,
  editorDone = {},
  coursesKnown = true,
  trialPreview = false,
}: {
  courses: readonly Pick<GraphqlCourseSummary, "id" | "slug" | "title" | "description" | "publishStatus" | "totalLessons">[];
  progress: readonly UserCourseProgress[];
  memberRole: Parameters<typeof canSeePathCloudEditor>[0];
  /** 学習パスの章を見せる人か (api-go の pathReady。layout の loader の pathEditorReady)。エディターだけの道場の章も同じ段で開く */
  pathReady?: boolean | null;
  audience?: PathCloudEditorAudience;
  /** クラウドエディターだけの道場の、章の ID ごとに通した手順の数。読めなかった章は無い (0 問済み) */
  editorDone?: Readonly<Record<string, number>>;
  /** コース一覧が読めたか。読めなかったときは、公開中のコースと重なるかを決められないので、クラウドエディターだけの道場は足さない */
  coursesKnown?: boolean;
  /** 体験の人か。体験の人には、エディターだけの道場も「見せるけれど使えない」鍵の札で並べる (体験の人には全部見せ、使う物だけ鍵の演出) */
  trialPreview?: boolean;
}): DojoCard[] {
  const published = courses.filter((course) => course.slug.startsWith("dojo-") && course.publishStatus === "published");
  const bySlug = new Map(published.map((course) => [course.slug, course]));
  const order = dojoSlugsInTrackOrder();
  const rest = published.map((course) => course.slug).filter((slug) => !order.includes(slug)).sort();
  const progressById = new Map(progress.map((row) => [row.courseId, row]));
  const showEditor = canSeePathCloudEditor(memberRole, pathReady, audience);
  const cards: DojoCard[] = [];
  for (const slug of [...order, ...rest]) {
    const course = bySlug.get(slug);
    if (!course) continue;
    const row = progressById.get(course.id);
    const total = row?.totalLessons || course.totalLessons || DEFAULT_TOTAL;
    const guideId = Object.hasOwn(DOJO_FIRST_GUIDES, slug) ? DOJO_FIRST_GUIDES[slug] : null;
    const { name, subtitle } = splitDojoTitle(course.title);
    cards.push({
      slug,
      title: course.title,
      name,
      subtitle,
      description: course.description?.trim() || null,
      icon: Object.hasOwn(DOJO_ICONS, slug) ? DOJO_ICONS[slug] : null,
      total,
      done: Math.min(row?.completedLessons ?? 0, total),
      href: `/learn/courses/${slug}`,
      cloudEditorHref: showEditor && guideId && findGuideEntry(guideId) ? cloudEditorGuideHref(guideId, "dojo") : null,
    });
  }
  if ((showEditor || trialPreview) && coursesKnown) {
    for (const id of editorOnlyDojoIds(courses)) {
      const dojo = findGuideEntry(id)?.dojo;
      if (!dojo) continue;
      const done = Object.hasOwn(editorDone, id) ? editorDone[id] : 0;
      cards.push({
        slug: id,
        title: `${dojo.name} ${dojo.subtitle}`,
        name: dojo.name,
        subtitle: dojo.subtitle,
        description: Object.hasOwn(EDITOR_DOJO_DESCRIPTIONS, id) ? EDITOR_DOJO_DESCRIPTIONS[id] : null,
        icon: Object.hasOwn(DOJO_ICONS, id) ? DOJO_ICONS[id] : null,
        total: DEFAULT_TOTAL,
        done: showEditor ? Math.max(0, Math.min(done, DEFAULT_TOTAL)) : 0,
        href: cloudEditorGuideHref(id, "dojo"),
        cloudEditorHref: null,
        ...(showEditor ? {} : { locked: true }),
      });
    }
  }
  return sortDojoCardsByLevel(cards);
}

const LEVEL_RANK: Readonly<Record<string, number>> = { 入門: 0, 初級: 1, 中級: 2, 上級: 3 };

/** 難易度の順 (入門から上級) に並べる。同じ難易度の中は、渡された順のまま (Array.sort は安定) */
export function sortDojoCardsByLevel<T extends { slug: string }>(cards: readonly T[]): T[] {
  const rank = (slug: string) => {
    const level = dojoLevelOf(slug);
    return level !== undefined && Object.hasOwn(LEVEL_RANK, level) ? LEVEL_RANK[level] : Object.keys(LEVEL_RANK).length;
  };
  return [...cards].sort((a, b) => rank(a.slug) - rank(b.slug));
}

/** loader が返す材料から、見せる役割でカードを作る。コース一覧が読めなかったとき (loadFailed) は coursesKnown を落とす */
export function buildDojoCardsFromLoader(
  data: {
    courses: Parameters<typeof buildDojoCards>[0]["courses"];
    progress: Parameters<typeof buildDojoCards>[0]["progress"];
    editorDone: Readonly<Record<string, number>>;
    loadFailed: boolean;
  },
  memberRole: Parameters<typeof canSeePathCloudEditor>[0],
  pathReady?: boolean | null,
  trialPreview = false,
): DojoCard[] {
  return buildDojoCards({ courses: data.courses, progress: data.progress, memberRole, pathReady, editorDone: data.editorDone, coursesKnown: !data.loadFailed, trialPreview });
}
